import React from 'react';
import Hero from '../components/Hero';
import CtaSection from '../components/CtaSection';
import StatsSection from '../components/StatsSection';
import TestimonialsSection from '../components/TestimonialsSection';
import {
  BrainCircuit,
  MessageSquare,
  Eye,
  BarChart3,
  Bot,
  Database,
  CircleDashed,
  Lightbulb,
  Shield,
  Rocket
} from 'lucide-react';

const AiServicesPage: React.FC = () => {

  const stats = [
    {
      value: '85%',
      label: 'Average Efficiency Improvement'
    },
    {
      value: '42%',
      label: 'Cost Reduction'
    },
    {
      value: '67%',
      label: 'Increased Accuracy'
    },
    {
      value: '50+',
      label: 'AI Experts on Team'
    }
  ];

  return (
    <>
      <Hero
        title="AI-Powered Solutions for the Future"
        subtitle="Leverage cutting-edge artificial intelligence to transform your business operations, enhance decision-making, and create new opportunities for growth."
        buttonText="Request a Demo"
        buttonLink="/contact"
        secondaryButtonText="Learn More"
        secondaryButtonLink="#benefits"
        backgroundStyle="gradient"
      />

      {/* AI Benefits */}
      <section id="benefits" className="py-16 md:py-24 bg-white scroll-mt-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Why Choose AI
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
              Transform Your Business with AI
            </h2>
            <p className="md:text-lg text-gray-600">
              Artificial intelligence offers unprecedented opportunities to innovate, optimize, and grow your business in today's competitive landscape.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                  <Rocket size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">Enhanced Efficiency</h3>
                <p className="text-gray-600">
                  Automate repetitive tasks and streamline operations to reduce costs and allow your team to focus on high-value activities.
                </p>
              </div>
            </div>

            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600">
                  <Lightbulb size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">Data-Driven Insights</h3>
                <p className="text-gray-600">
                  Uncover valuable patterns and trends in your data to make more informed decisions and identify new opportunities.
                </p>
              </div>
            </div>

            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600">
                  <Shield size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">Improved Accuracy</h3>
                <p className="text-gray-600">
                  Reduce human error and enhance precision in critical processes with AI-powered verification and validation.
                </p>
              </div>
            </div>

            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
                  <Database size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">Predictive Capabilities</h3>
                <p className="text-gray-600">
                  Anticipate market trends, customer behavior, and potential issues before they occur to stay ahead of the competition.
                </p>
              </div>
            </div>

            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center text-cyan-600">
                  <Eye size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">Enhanced Customer Experience</h3>
                <p className="text-gray-600">
                  Deliver personalized, responsive, and intuitive experiences to your customers through AI-powered interactions and recommendations.
                </p>
              </div>
            </div>

            <div className="flex">
              <div className="flex-shrink-0 mr-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600">
                  <Bot size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">Competitive Advantage</h3>
                <p className="text-gray-600">
                  Stay ahead in your industry by leveraging cutting-edge AI technologies that transform how you operate and serve your customers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <StatsSection
        stats={stats}
        sectionStyle="gradient"
      />

      {/* AI Services */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Our AI Services
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
              Comprehensive AI Solutions
            </h2>
            <p className="md:text-lg text-gray-600">
              We offer a wide range of AI services tailored to meet your specific business needs and challenges.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-16">
            {/* Machine Learning */}
            <div id="ml" className="scroll-mt-24">
              <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                <div className="w-full md:w-1/2">
                  <div className="rounded-xl overflow-hidden shadow-xl">
                    <div className="aspect-video bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center">
                      <BrainCircuit size={80} className="text-white opacity-50" />
                    </div>
                  </div>
                </div>
                <div className="px-4 md:w-1/2">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-blue-100 text-blue-600 mr-4">
                      <BrainCircuit size={24} />
                    </div>
                    <h3 className="text-xl md:text-3xl font-bold text-gray-900">Machine Learning</h3>
                  </div>
                  <p className="text-justify text-gray-600 mb-6">
                    Our machine learning services help you build intelligent systems that learn from data and improve over time. We develop custom ML models to solve complex business problems and automate decision-making processes.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Supervised and unsupervised learning models</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Predictive analytics and forecasting</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Recommendation systems</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Anomaly detection and fraud prevention</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Custom model training and deployment</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Natural Language Processing */}
            <div id="nlp" className="scroll-mt-24">
              <div className="flex flex-col md:flex-row-reverse items-center gap-8 md:gap-12">
                <div className="w-full md:w-1/2">
                  <div className="rounded-xl overflow-hidden shadow-xl">
                    <div className="aspect-video bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center">
                      <MessageSquare size={80} className="text-white opacity-50" />
                    </div>
                  </div>
                </div>
                <div className="px-4 md:w-1/2">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 mr-4">
                      <MessageSquare size={24} />
                    </div>
                    <h3 className="text-xl md:text-3xl font-bold text-gray-900">Natural Language Processing</h3>
                  </div>
                  <p className="text-justify text-gray-600 mb-6">
                    Our NLP services enable your systems to understand, interpret, and generate human language. We help you extract insights from text data and build intelligent conversational interfaces.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-indigo-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Sentiment analysis and opinion mining</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-indigo-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Chatbot and virtual assistant development</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-indigo-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Text classification and categorization</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-indigo-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Named entity recognition</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-indigo-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Document summarization and content generation</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Computer Vision */}
            <div id="vision" className="scroll-mt-24">
              <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                <div className="w-full md:w-1/2">
                  <div className="rounded-xl overflow-hidden shadow-xl">
                    <div className="aspect-video bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                      <Eye size={80} className="text-white opacity-50" />
                    </div>
                  </div>
                </div>
                <div className="px-4 md:w-1/2">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-purple-100 text-purple-600 mr-4">
                      <Eye size={24} />
                    </div>
                    <h3 className="text-xl md:text-3xl font-bold text-gray-900">Computer Vision</h3>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Our computer vision services enable machines to interpret and understand visual information from the world. We develop image recognition and processing solutions for automation and enhanced decision-making.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-purple-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Object detection and recognition</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-purple-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Facial recognition and analysis</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-purple-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Image segmentation and classification</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-purple-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Visual quality control and inspection</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-purple-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Video analysis and motion detection</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Analytics */}
            <div id="analytics" className="scroll-mt-24">
              <div className="flex flex-col md:flex-row-reverse items-center gap-8 md:gap-12">
                <div className="w-full md:w-1/2">
                  <div className="rounded-xl overflow-hidden shadow-xl">
                    <div className="aspect-video bg-gradient-to-r from-cyan-500 to-teal-500 flex items-center justify-center">
                      <BarChart3 size={80} className="text-white opacity-50" />
                    </div>
                  </div>
                </div>
                <div className="px-4 md:w-1/2">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-cyan-100 text-cyan-600 mr-4">
                      <BarChart3 size={24} />
                    </div>
                    <h3 className="text-xl md:text-3xl font-bold text-gray-900">AI-Powered Data Analytics</h3>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Our AI-powered data analytics services help you uncover valuable insights from your data to make informed decisions. We create custom analytics solutions that transform raw data into actionable business intelligence.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-cyan-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Predictive and prescriptive analytics</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-cyan-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Customer behavior analysis</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-cyan-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Real-time analytics dashboards</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-cyan-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Market trend analysis and forecasting</p>
                    </div>
                    <div className="flex items-start">
                      <div className="mt-1 mr-3 text-cyan-600">
                        <CircleDashed size={18} />
                      </div>
                      <p className="text-gray-700">Business performance optimization</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Our Methodology
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
              AI Implementation Process
            </h2>
            <p className="md:text-lg text-gray-600">
              We follow a proven, systematic approach to deliver successful AI solutions that meet your business objectives.
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-200 hidden md:block"></div>

            <div className="space-y-16 relative">
              {/* Step 1 */}
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 md:pr-16 mb-8 md:mb-0 text-center md:text-right">
                  <h3 className="text-xl md:text-2xl font-bold mb-3 text-gray-900">Discovery & Assessment</h3>
                  <p className="text-gray-600">
                    We begin by understanding your business objectives, challenges, and data assets to identify the most impactful AI opportunities.
                  </p>
                </div>
                <div className="md:absolute md:left-1/2 md:transform md:-translate-x-1/2 z-10 bg-white rounded-full p-2 border-4 border-blue-500 mb-8 md:mb-0">
                  <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-xl">
                    1
                  </div>
                </div>
                <div className="md:w-1/2 md:pl-16">
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>Business needs analysis</span>
                    </li>
                    <li className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>Data readiness assessment</span>
                    </li>
                    <li className="flex items-start">
                      <div className="mt-1 mr-3 text-blue-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>AI opportunity identification</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex flex-col md:flex-row-reverse items-center">
                <div className="md:w-1/2 md:pl-16 mb-8 md:mb-0 text-center md:text-left">
                  <h3 className="text-xl md:text-2xl font-bold mb-3 text-gray-900">Strategy & Planning</h3>
                  <p className="text-gray-600">
                    We develop a comprehensive AI strategy and roadmap aligned with your business goals, outlining the approach, technologies, and timeline.
                  </p>
                </div>
                <div className="md:absolute md:left-1/2 md:transform md:-translate-x-1/2 z-10 bg-white rounded-full p-2 border-4 border-indigo-500 mb-8 md:mb-0">
                  <div className="w-12 h-12 rounded-full bg-indigo-500 text-white flex items-center justify-center font-bold text-xl">
                    2
                  </div>
                </div>
                <div className="md:w-1/2 md:pr-16 md:text-right">
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start md:flex-row-reverse">
                      <div className="mt-1 ml-0 mr-3 md:ml-3 md:mr-0 text-indigo-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>AI solution design</span>
                    </li>
                    <li className="flex items-start md:flex-row-reverse">
                      <div className="mt-1 ml-0 mr-3 md:ml-3 md:mr-0 text-indigo-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>Technology selection</span>
                    </li>
                    <li className="flex items-start md:flex-row-reverse">
                      <div className="mt-1 ml-0 mr-3 md:ml-3 md:mr-0 text-indigo-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>Implementation roadmap</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 md:pr-16 mb-8 md:mb-0 text-center md:text-right">
                  <h3 className="text-xl md:text-2xl font-bold mb-3 text-gray-900">Development & Training</h3>
                  <p className="text-gray-600">
                    We build and train custom AI models using your data, focusing on accuracy, scalability, and performance to meet your specific requirements.
                  </p>
                </div>
                <div className="md:absolute md:left-1/2 md:transform md:-translate-x-1/2 z-10 bg-white rounded-full p-2 border-4 border-purple-500 mb-8 md:mb-0">
                  <div className="w-12 h-12 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold text-xl">
                    3
                  </div>
                </div>
                <div className="md:w-1/2 md:pl-16">
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <div className="mt-1 mr-3 text-purple-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>Data preparation and cleaning</span>
                    </li>
                    <li className="flex items-start">
                      <div className="mt-1 mr-3 text-purple-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>Model development and training</span>
                    </li>
                    <li className="flex items-start">
                      <div className="mt-1 mr-3 text-purple-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>Iterative testing and refinement</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex flex-col md:flex-row-reverse items-center">
                <div className="md:w-1/2 md:pl-16 mb-8 md:mb-0 text-center md:text-left">
                  <h3 className="text-xl md:text-2xl font-bold mb-3 text-gray-900">Integration & Deployment</h3>
                  <p className="text-gray-600">
                    We seamlessly integrate AI solutions into your existing systems and deploy them in your preferred environment, ensuring smooth operation.
                  </p>
                </div>
                <div className="md:absolute md:left-1/2 md:transform md:-translate-x-1/2 z-10 bg-white rounded-full p-2 border-4 border-teal-500 mb-8 md:mb-0">
                  <div className="w-12 h-12 rounded-full bg-teal-500 text-white flex items-center justify-center font-bold text-xl">
                    4
                  </div>
                </div>
                <div className="md:w-1/2 md:pr-16 md:text-right">
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start md:flex-row-reverse">
                      <div className="mt-1 ml-0 mr-3 md:ml-3 md:mr-0 text-teal-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>System integration</span>
                    </li>
                    <li className="flex items-start md:flex-row-reverse">
                      <div className="mt-1 ml-0 mr-3 md:ml-3 md:mr-0 text-teal-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>Production deployment</span>
                    </li>
                    <li className="flex items-start md:flex-row-reverse">
                      <div className="mt-1 ml-0 mr-3 md:ml-3 md:mr-0 text-teal-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>Performance optimization</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex flex-col md:flex-row items-center">
                <div className="md:w-1/2 md:pr-16 mb-8 md:mb-0 text-center md:text-right">
                  <h3 className="text-xl md:text-2xl font-bold mb-3 text-gray-900">Monitoring & Improvement</h3>
                  <p className="text-gray-600">
                    We provide ongoing monitoring, maintenance, and continuous improvement to ensure your AI solutions remain effective and adapt to changing conditions.
                  </p>
                </div>
                <div className="md:absolute md:left-1/2 md:transform md:-translate-x-1/2 z-10 bg-white rounded-full p-2 border-4 border-cyan-500 mb-8 md:mb-0">
                  <div className="w-12 h-12 rounded-full bg-cyan-500 text-white flex items-center justify-center font-bold text-xl">
                    5
                  </div>
                </div>
                <div className="md:w-1/2 md:pl-16">
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-start">
                      <div className="mt-1 mr-3 text-cyan-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>Performance monitoring</span>
                    </li>
                    <li className="flex items-start">
                      <div className="mt-1 mr-3 text-cyan-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>Model retraining and updating</span>
                    </li>
                    <li className="flex items-start">
                      <div className="mt-1 mr-3 text-cyan-600">
                        <CircleDashed size={16} />
                      </div>
                      <span>Continuous improvement</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaSection
        title="Ready to Transform Your Business with AI?"
        subtitle="Contact us today to discuss your AI needs and discover how we can help you leverage the power of artificial intelligence."
        buttonText="Get in Touch"
        buttonLink="/contact"
        sectionStyle="gradient"
      />
    </>
  );
};

export default AiServicesPage;
